import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from './api'
import { connectChat, connectNotifications, disconnectChat, disconnectNotifications } from './signalr'

interface User {
  id: string
  email: string
  name: string
  phone?: string
  role: string // Can be 'User', 'Admin', 'USER', 'ADMIN'
  avatarUrl?: string
  createdAt?: string
}

interface AuthState {
  user: User | null
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  register: (data: { email: string; password: string; name: string; phone?: string; termsAccepted: boolean }) => Promise<void>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>
  clearError: () => void
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/api/auth/login', { email, password })
          const apiResponse = response.data
          const { user } = apiResponse.data || apiResponse

          set({
            user,
            isLoading: false,
          })

          // Connect SignalR hubs - tokens are now in HttpOnly cookies
          // We'll need to update SignalR connection to use cookie auth
          // For now, we can try to connect without explicit token
          connectChat().catch(console.error)
          connectNotifications().catch(console.error)
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Chyba při přihlášení',
            isLoading: false,
          })
          throw error
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post('/api/auth/register', data)
          const apiResponse = response.data
          const { user } = apiResponse.data || apiResponse

          set({
            user,
            isLoading: false,
          })

          // Connect SignalR hubs
          connectChat().catch(console.error)
          connectNotifications().catch(console.error)
        } catch (error: any) {
          set({
            error: error.response?.data?.message || 'Chyba při registraci',
            isLoading: false,
          })
          throw error
        }
      },

      logout: async () => {
        try {
          // Refresh token is in HttpOnly cookie, backend will handle it
          await api.post('/api/auth/logout')
        } catch (error) {
          // Ignore logout errors
        } finally {
          // Disconnect SignalR hubs
          disconnectChat()
          disconnectNotifications()

          set({
            user: null,
          })
        }
      },

      fetchUser: async () => {
        try {
          const response = await api.get('/api/auth/me')
          const apiResponse = response.data
          set({ user: apiResponse.data || apiResponse })

          // Connect SignalR hubs - cookies will be sent automatically
          connectChat().catch(console.error)
          connectNotifications().catch(console.error)
        } catch (error) {
          set({ user: null })
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        // NOTE: Tokens are no longer stored in localStorage - they're in HttpOnly cookies
      }),
    }
  )
)
